import React from 'react';
import { Globe, Mail, MapPin, Phone, User } from 'lucide-react';
import { PublicOrganizer } from '@radevents/shared';
import { LetterheadRow } from './OrganizerLetterhead';

/**
 * Kontakt des Veranstalters für Besucher (30.10.2026).
 *
 * **Eine Liste, zwei Gestalten**, umgeschaltet in `App.css` (`.org-kontakt`): am Rechner Zeilen
 * mit Symbol, Wert und Beschriftung in der Seitenspalte; auf dem Handy eine wischbare Reihe von
 * Knöpfen direkt unter dem Kopf („Website", „Route", „Anrufen"), wie bei Google Maps und Luma.
 * Dort zählt die Handlung, nicht der Wert, und die Liste stünde sonst über den Events.
 *
 * Der Verein selbst sieht weiter den Briefkopf (`OrganizerLetterhead`): dort steht, wer welche
 * Angabe sieht, und dort wird bearbeitet.
 */
const ICONS: Record<string, React.ReactNode> = {
  Website: <Globe size={17} aria-hidden />,
  'E-Mail': <Mail size={17} aria-hidden />,
  Telefon: <Phone size={17} aria-hidden />,
  Anschrift: <MapPin size={17} aria-hidden />,
  Ansprechpartner: <User size={17} aria-hidden />,
};

/** Was auf dem Handy-Knopf steht: die Handlung, nicht die Angabe. */
const ACTION: Record<string, string> = {
  Website: 'Website', 'E-Mail': 'E-Mail', Telefon: 'Anrufen', Anschrift: 'Route',
};

type SocialKey = 'stravaClubUrl' | 'instagramUrl' | 'facebookUrl' | 'komootUrl';
const SOCIALS: { key: SocialKey; label: string }[] = [
  { key: 'instagramUrl', label: 'Instagram' },
  { key: 'stravaClubUrl', label: 'Strava' },
  { key: 'komootUrl', label: 'Komoot' },
  { key: 'facebookUrl', label: 'Facebook' },
];

interface Props {
  rows: LetterheadRow[];
  organizer: Pick<PublicOrganizer, SocialKey>;
}

const OrganizerKontakt: React.FC<Props> = ({ rows, organizer }) => {
  // Region steht schon im Kopf, und ohne Wert gibt es nichts zu zeigen.
  const shown = rows.filter(r => r.value && r.label !== 'Region');
  const socials = SOCIALS.filter(s => organizer[s.key]);
  if (shown.length === 0 && socials.length === 0) return null;

  return (
    <section className="org-kontakt" aria-label="Kontakt">
      <h2 className="org-kontakt-titel">Kontakt</h2>
      <ul className="org-kontakt-liste">
        {shown.map(r => {
          const inner = (
            <>
              <span className="org-kontakt-symbol">{ICONS[r.label]}</span>
              <span className="org-kontakt-text">
                <span className="org-kontakt-wert">{r.value}</span>
                <span className="org-kontakt-aktion">{ACTION[r.label] ?? r.label}</span>
              </span>
            </>
          );
          return (
            <li key={r.label} className={r.href ? undefined : 'org-kontakt-nurtext'}>
              {r.href ? (
                <a className="org-kontakt-zeile" href={r.href} title={r.hint}
                  {...(r.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{inner}</a>
              ) : <div className="org-kontakt-zeile">{inner}</div>}
            </li>
          );
        })}
        {socials.map(s => (
          <li key={s.key} className="org-kontakt-sozial">
            <a className="org-kontakt-zeile" href={organizer[s.key]} target="_blank" rel="noopener noreferrer">
              <span className="org-kontakt-text"><span className="org-kontakt-aktion">{s.label}</span></span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default OrganizerKontakt;
